<script lang="ts">
  import { onMount } from "svelte";

  // components
  import FulfillmentMethodBanner from "@retailer/svelte/Shared/FulfillmentMethodBanner.svelte";
  import FulfillmentMethodPicker from "@retailer/svelte/Shared/FulfillmentMethodPicker.svelte";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import SellSheetBuyProductList from "./SellSheetBuyProductList.svelte";

  // services
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { DistributorEntity, SellSheetEntity } from "@shared/services/event_tracking/entities";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { isAccountNumberRequired } from "@retailer/services/product_card_service";

  // types
  import type { SellSheet } from "@shared/types/SellSheet";
  import type { Product } from "@shared/types/ProductLine";

  export let isSFList: boolean;
  export let sellSheet: SellSheet;
  export let showProductLinks = false;
  export const user = sellSheet.user;
  export const distributor = sellSheet.distributor;

  const { trackClick, trackEvent } = EventTracking;

  const usesFulfillmentMethodBanner =
    sellSheet.product_lines.length > 0 &&
    shouldUseRetailerFulfillmentMethodForDistributor(
      distributor?.id,
      distributor?.uses_warehouse_pickup,
      stagedRolloutCheck("marketplace__fulfillment_methods") && distributor?.uses_wine_cellar,
    ) &&
    !hideFulfillmentMethodPickerForDistributor(distributor?.id);

  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );

  const needsAccountNumber = async () => {
    if (!usesFulfillmentMethodBanner) return false;
    if (!wineCellarStagedRolloutEnabled)
      return (
        distributor?.requires_account_number &&
        !distributor?.is_rolling_out_integrated_orders &&
        !distributor?.account_number
      );

    const sku = sellSheet.product_lines[0].sell_sheet_products_attributes[0].sku;
    const response = await isAccountNumberRequired(
      distributor?.id,
      sku,
      null,
      distributor?.account_number,
    );
    return response || false;
  };

  let showFulfillmentMethodsDrawer: boolean = false;

  const trackContactClick = (type: string) => {
    return () => {
      trackClick(type, [
        new DistributorEntity({ id: distributor?.id }),
        new SellSheetEntity({ sheetId: sellSheet.id }),
      ]);
    };
  };

  function openFulfillmentMethodsDrawer(_e: CustomEvent<number>): void {
    showFulfillmentMethodsDrawer = true;
  }

  onMount(() => {
    trackEvent("sell_sheet_page_view", [
      new DistributorEntity({ id: distributor?.id }),
      new SellSheetEntity({ sheetId: sellSheet.id }),
    ]);
  });

  let products: Product[] = [];
  $: products = sellSheet.product_lines.map(pl => pl.sell_sheet_products_attributes).flat();
</script>

<PageWrapper let:mobileMode>
  <div class="grid-container">
    <!-- NAME -->
    <div
      class="grid-row padding-left--small padding-right--small padding-bottom--small padding-bottom--medium--md"
    >
      <div class="grid-col c12-12 align-center">
        <div
          class="default-headline--regular--md default-title--bold margin-top--small padding-top--small--md wrap-text"
        >
          {sellSheet.name}
        </div>
      </div>
    </div>
    <!-- REP CONTACT INFO -->
    {#await needsAccountNumber() then needsAccountNumber}
      <div class="grid-row">
        <div class="grid-col c12-12 background-white padding--small sell-sheet-preview-wrapper">
          <div
            class="grid-row"
            class:fizz-stack-24={usesFulfillmentMethodBanner && !needsAccountNumber}
          >
            <div class="grid-col c8-12--md c12-12">
              <div class="default-subheader--bold">{distributor?.name}</div>
              <div class="default-subheader--regular">{user.first_name} {user.last_name}</div>
              <div class="default-subheader--bold color-brandSecondary">
                {#if user.phone}
                  <a href="tel:{user.phone}" on:click={trackContactClick("sales_rep_phone_number")}
                    >{user.phone || ""}</a
                  >
                  <span class="color-black default-body--regular"> , </span>
                {/if}
                <a href="mailto:{user.email}" on:click={trackContactClick("sales_rep_email")}
                  >{user.email}</a
                >
              </div>
            </div>
            {#if distributor?.cloudinary_image_url}
              <div class="grid-col c4-12--md align-right show--md">
                <div class="media margin-left--small float-right">
                  <ProductImage
                    width={180}
                    height={100}
                    product={{ cloudinary_image_url: distributor?.cloudinary_image_url }}
                    className={"vertical-align-text-top media-fixed distributors-image"}
                  />
                </div>
              </div>
            {/if}
          </div>
          {#if usesFulfillmentMethodBanner && !needsAccountNumber}
            <FulfillmentMethodBanner
              {distributor}
              {products}
              on:openDrawer={openFulfillmentMethodsDrawer}
            />
            <FulfillmentMethodPicker
              {distributor}
              {products}
              forProductLine={false}
              bind:open={showFulfillmentMethodsDrawer}
            />
          {/if}
        </div>
      </div>
    {/await}
    <div class="grid-row"><hr class="hr hr-card-separator" /></div>
    {#if sellSheet.notes}
      <div class="grid-row">
        <div class="grid-col c12-12 background-white padding--small sell-sheet-preview-wrapper">
          <span class="default-subheader--bold" style="display: block;">Notes:</span>
          <span class="default-subheader--regular wrap-text" style="white-space: pre-wrap;"
            >{sellSheet.notes}</span
          >
        </div>
      </div>
    {/if}

    <!-- PRODUCTS -->
    <div class="grid-row"><hr class="hr hr-card-separator" /></div>
    <SellSheetBuyProductList {showProductLinks} {sellSheet} {mobileMode} {isSFList} />
  </div>
</PageWrapper>
